import { useTranslation } from 'react-i18next';
import { Row, Col, Button, CardBody } from 'reactstrap';
import Select from 'react-select';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { InputActionMeta, Option, SelectedData } from 'types/common/FormTypes';
import { transformOptionsToListsArray } from 'utils/common/handleSelect';
import { CheckboxOption } from 'Components/pos2u/CustomReactSelectComponents/CustomReactSelectOption';

import { useSelector } from 'react-redux';
import { RootState } from 'slices';
import { useDispatch } from 'react-redux';

import { MultiSelectSingleLineStyles } from 'styles/common/multiSelectSingleLineStyle';
import { CustomBranchMenu } from 'Components/pos2u/CustomReactSelectComponents/CustomReactSelectBranchMenu';
import TextSelectedItems from 'Components/pos2u/TextSelectedItems';

import {
  setCurrentPage,
  setInputPageValue,
  setPageFilters,
} from 'slices/timeAttendance/reducer';
import Flatpickr from 'react-flatpickr';
import { Thai } from 'flatpickr/dist/l10n/th';
import { TimeAttendanceSearchFilters } from 'types/timeAttendance/timeAttendanceTypes';
import { TimeAttendanceSearchValidationSchema } from 'pages/TimeAttendance/TimeAttendanceSearchValidation';
import { toast, ToastContainer } from 'react-toastify';
import { CustomReactSelectStyles } from 'styles/common/singleSelectStyle';
import { getTimeAttendanceOfficers } from 'services/timeAttendanceService';
import { mapOfficersToSelectOptions } from 'utils/mappers/timeAttendanceMapper';
import { CalendarFormats } from 'pages/TimeAttendance/constants';
// import 'react-toastify/dist/ReactToastify.css';

interface TimeAttendanceSearchFormProps {
  isDarkMode: boolean;
  onSubmit: (value: TimeAttendanceSearchFilters) => void;
  selectedCheckBoxDelete: number;
}

const TimeAttendanceSearchForm = ({
  isDarkMode,
  onSubmit,
  selectedCheckBoxDelete,
}: TimeAttendanceSearchFormProps) => {
  const { t, i18n } = useTranslation();
  const infoState = useSelector((state: RootState) => state.Info);
  const { branches } = infoState;
  const [officerOptions, setOfficerOptions] = useState<
    Array<Option | { label: string; value: null }>
  >([]);
  const dateRangeRef = useRef<any>(null);

  const getLocale = useCallback(() => {
    switch (i18n.language) {
      case 'th':
        return Thai;

      default:
        return undefined;
    }
  }, [i18n.language]);

  // const customerState = useSelector((state: RootState) => state.Customer);
  const timeAttendanceState = useSelector(
    (state: RootState) => state.TimeAttendance
  );
  const { PageFilters } = timeAttendanceState;

  const dispatch = useDispatch();

  const formik = useFormik<TimeAttendanceSearchFilters>({
    initialValues: PageFilters,
    validationSchema: TimeAttendanceSearchValidationSchema(t),
    enableReinitialize: true,
    onSubmit: (values) => {
      dispatch(setPageFilters(values));
      dispatch(setCurrentPage(0));
      dispatch(setInputPageValue(1));
      onSubmit(values);
    },
  });

  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    const fetchOfficerOptions = async () => {
      try {
        const response = await getTimeAttendanceOfficers();

        if (response?.message === 'OK' && response.error_code === 0) {
          setOfficerOptions(
            mapOfficersToSelectOptions(response.data.officers, t)
          );
          return;
        }

        setOfficerOptions([{ label: t('All'), value: null }]);
      } catch (error) {
        console.error('Failed to fetch time attendance officers:', error);
        setOfficerOptions([{ label: t('All'), value: null }]);
      }
    };

    fetchOfficerOptions();
  }, [t]);

  const handleInputChange = (newValue: string, meta: InputActionMeta) => {
    if (meta.action === 'input-change') {
      setInputValue(newValue);
    }
  };
  const handleChangeBranch = (selectedOptions: SelectedData[]) => {
    formik
      .setFieldValue(`branch`, transformOptionsToListsArray(selectedOptions))
      .then(async () => formik.setFieldTouched(`branch`, true));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const marginStyle = useMemo(() => {
    return `me-2 mb-2`;
  }, []);
  const gapStyle = useMemo(() => {
    return `g-0`;
  }, []);
  const errorTextStyle = {
    minHeight: '1.5rem',
  };
  const minSelectableDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const minDate = new Date(today);
    minDate.setFullYear(today.getFullYear() - 3);

    return minDate;
  }, []);
  const maxSelectableDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return today;
  }, []);

  const parseLocalDate = useCallback((dateString: string) => {
    const [year, month, day] = dateString.split('-').map(Number);

    return new Date(year, month - 1, day);
  }, []);

  useEffect(() => {
    const flatpickrInstance = dateRangeRef.current?.flatpickr;

    if (!flatpickrInstance) {
      return;
    }

    flatpickrInstance.set('minDate', minSelectableDate);
    flatpickrInstance.set('maxDate', maxSelectableDate);
  }, [maxSelectableDate, minSelectableDate]);

  // const dateRangeValue =
  //   formik.values.startDate && formik.values.endDate
  //     ? [formik.values.startDate, formik.values.endDate]
  //     : [];
  const dateRangeValue =
    formik.values.startDate && formik.values.endDate
      ? [
          parseLocalDate(formik.values.startDate),
          parseLocalDate(formik.values.endDate),
        ]
      : [];

  return (
    <Row className="mb-3">
      <CardBody className="border border-dashed border-end-0 border-start-0 my-0">
        <form
          onSubmit={handleSubmit}
          data-testid="TimeAttendanceSearchForm"
          noValidate
        >
          <Row className={`${gapStyle}`}>
            <Col md={10} lg={10}>
              <Row className={`${gapStyle}`}>
                <Col md={4} lg={4} className="col-xxl-4 ">
                  <div
                    className={`${marginStyle}`}
                    data-testid="search-form-branch"
                  >
                    <Flatpickr
                      ref={dateRangeRef}
                      id="dateRange"
                      name="dateRange"
                      className="form-control"
                      options={{
                        mode: 'range',
                        // dateFormat: 'Y-m-d',
                        dateFormat: CalendarFormats.dateFormat,
                        locale: getLocale(),
                        onChange: function (selectedDates, _dateStr, instance) {
                          if (selectedDates.length === 2) {
                            const [start, end] = selectedDates;

                            const diffTime = end.getTime() - start.getTime();
                            const diffDays = diffTime / (1000 * 60 * 60 * 24);

                            if (diffDays > 30) {
                              toast.error(
                                t('CannotSelectMoreDays', { amount: 31 }),
                                {
                                  autoClose: 3000,
                                }
                              );

                              const maxEnd = new Date(start);
                              maxEnd.setDate(start.getDate() + 30);

                              instance.setDate([start, maxEnd], true);
                            }
                          }
                        },
                      }}
                      value={dateRangeValue}
                      onChange={(dates) => {
                        const formatDate = (date: Date) =>
                          [
                            date.getFullYear(),
                            String(date.getMonth() + 1).padStart(2, '0'),
                            String(date.getDate()).padStart(2, '0'),
                          ].join('-');

                        if (dates.length === 1) {
                          const nextStartDate = formatDate(dates[0]);

                          formik.setFieldValue('startDate', nextStartDate);
                          formik.setFieldValue('endDate', '');

                          return;
                        }

                        if (dates.length === 2) {
                          const nextStartDate = formatDate(dates[0]);
                          const nextEndDate = formatDate(dates[1]);

                          formik.setFieldValue('startDate', nextStartDate);
                          formik.setFieldValue('endDate', nextEndDate);
                        }
                      }}
                      onClick={() => {
                        formik.setFieldTouched('startDate', true);
                        formik.setFieldTouched('endDate', true);
                      }}
                      placeholder={t('Select date range')}
                    />

                    <div className="text-danger" style={errorTextStyle}>
                      {(formik.touched.startDate && formik.errors.startDate) ||
                      (formik.touched.endDate && formik.errors.endDate)
                        ? formik.errors.startDate || formik.errors.endDate
                        : ''}
                    </div>
                  </div>
                </Col>
                <Col md={4} lg={4} className="col-xxl-4 ">
                  <div
                    className={`${marginStyle}`}
                    data-testid="search-form-branch"
                  >
                    <Select
                      id="branch"
                      name="branch"
                      aria-label="branch"
                      value={branches.filter(
                        (option) =>
                          Array.isArray(formik.values.branch) &&
                          formik.values.branch.includes(option.value)
                      )}
                      isMulti={true}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      inputValue={inputValue}
                      onInputChange={handleInputChange}
                      onChange={handleChangeBranch}
                      onBlur={() => {
                        formik.setFieldTouched('branch', true);
                      }}
                      options={branches}
                      components={{
                        Menu: CustomBranchMenu,
                        Option: CheckboxOption,
                      }}
                      styles={MultiSelectSingleLineStyles(isDarkMode)}
                      placeholder={t('Branch')}
                    />
                    <div className="text-danger" style={errorTextStyle}>
                      {formik.touched.branch && formik.errors.branch
                        ? String(formik.errors.branch)
                        : ''}
                    </div>
                  </div>
                </Col>
                <Col md={4} lg={4} className="col-xxl-4 ">
                  <div className={`${marginStyle}`}>
                    <Select
                      styles={CustomReactSelectStyles(isDarkMode)}
                      id="officeID"
                      name="officeID"
                      options={officerOptions}
                      value={
                        officerOptions.find(
                          (option) => option.value === formik.values.officeID
                        ) ?? null
                      }
                      onChange={(option: any) => {
                        formik.setFieldValue('officeID', option?.value ?? null);
                        formik.setFieldTouched('officeID', true);
                      }}
                      onBlur={() => {
                        formik.setFieldTouched('officeID', true);
                      }}
                      placeholder={t('Employee')}
                    />
                  </div>
                </Col>
              </Row>
            </Col>
            <Col md={2} lg={2}>
              <div className="d-flex mx-lg-2 mx-md-0">
                <Button
                  color="success"
                  type="button"
                  outline
                  data-testid="search-form-btn-submit"
                  onClick={() => {
                    formik.submitForm();
                  }}
                >
                  {t('Search')}
                </Button>
              </div>
            </Col>
          </Row>
        </form>
        {selectedCheckBoxDelete && selectedCheckBoxDelete > 0 ? (
          <TextSelectedItems selectedItems={selectedCheckBoxDelete} />
        ) : null}
      </CardBody>
      <ToastContainer closeButton={false} limit={1} />
    </Row>
  );
};
export default TimeAttendanceSearchForm;
